/*
 * KYC Name Match Engine — Indian Context (Audit-Friendly)
 * PAN ↔ Bank Account Name Matching
 *
 * Reference: Rules applied in runtime sequence (for audit/legal review)
 * 1) Normalize both names:
 *    - lowercase conversion,
 *    - '&' -> 'and',
 *    - punctuation separators converted to spaces: . / , \ ( ) -,
 *    - any remaining non-alphabetic characters converted to spaces,
 *    - repeated spaces collapsed and trimmed.
 * 2) Tokenize and preprocess (remove configured honorifics/suffixes,
 *    canonicalize synonyms like company -> co, remove connector tokens like and).
 * 2b) Truncate at relational markers (S/O, D/O, W/O = Son/Daughter/Wife Of)
 *     — everything after the marker is a different person's name.
 * 3) Reject if either side has zero tokens after preprocessing.
 * 4) Choose shorter/longer side by token count (tie: PAN is treated as longer).
 * 5) Match pass 1: exact token matches only (one-to-one).
 * 6) Match pass 2: non-exact deterministic matches only:
 *    - initials/prefix (1-2 letter short token matching start of longer token),
 *    - explicit known abbreviations only: md, mohd
 *      (for Mohammad/Mohammed/Muhammad/Muhammed variants).
 * 6c) Match pass 3 (fuzzy fallback — applied to ALL shorter-side tokens that
 *    are still unmatched after passes 1 & 2):
 *    - For each unmatched shorter token, compare against every unmatched longer
 *      token using character-frequency analysis.
 *    - ACCEPT the pair when:
 *        a) all characters are the same (anagram — sorted chars are equal), OR
 *        b) at most 1 character is unaccounted for AND >60 % of characters match
 *           (matchedChars / max(len1, len2) > 0.60).
 *    - Minimum token length of 4 required to avoid false positives on short tokens.
 *    - ACCEPT is given only if ALL shorter-side tokens end up matched (including
 *      those matched via fuzzy). Partial fuzzy matches alone do not suffice.
 * 6d) Match pass 4 (merged-initials reconciliation — fires after passes 1–3):
 *    - Applied to every shorter-side token still unmatched with length 2 or 3.
 *    - For each such token, ALL combinations of that many unused longer-side
 *      tokens are generated (C(n,k) where k=token length, k ≤ 3 so at most
 *      C(n,3) combinations — safe even with n up to ~8 tokens).
 *    - A combination wins when each character of the merged token is the first
 *      letter of a distinct token in that combination (order-insensitive).
 *    - First winning combination is used; consumed longer tokens are marked used.
 *    - Extra unused longer tokens after matching are non-blocking.
 *    - Example: "Nithin KP" vs "KAMANAHALLI PARAMESWRAPPA NITHIN":
 *      after "nithin" matches, "kp"(2) tries combos of 2 from the 2 unused
 *      longer tokens → ["kamanahalli","parameswrappa"] wins → ACCEPT.
 *    - Also resolves "Pavana KP" vs "Pavana K P" ("kp" vs ["k","p"]).
 * 7) Compute unmatched tokens on both sides and reordered-name indicator.
 * 8) ACCEPT only if all shorter-side tokens matched and longer-side extras are
 *    within configured defaults (extra tokens allowed by default, max 2). Otherwise REJECT.
 */

// ─── TYPES ───────────────────────────────────────────────────────────────────

export type MatchMethod =
  | "exact"
  | "initial_match"
  | "known_abbreviation"
  | "known_variant"
  | "fuzzy_char_match"
  | "merged_initials";

export type Verdict = "ACCEPT" | "REJECT";

export interface TokenMatchDetail {
  panToken: string;
  bankToken: string;
  method: MatchMethod;
}

export interface MatchResult {
  verdict: Verdict;
  /** Number of tokens that matched out of total */
  matchedCount: number;
  totalTokens: number;
  /** true if all tokens matched but in different order */
  reordered: boolean;
  /** Human-readable reasoning for each decision factor */
  reasons: string[];
  /** Per-token comparison details */
  details: TokenMatchDetail[];
  /** Normalized PAN tokens used */
  panTokens: string[];
  /** Normalized Bank tokens used */
  bankTokens: string[];
}

export interface MatchConfig {
  /**
   * If true, every token in the shorter name must match a token
   * in the longer name, but extra tokens in the longer name are
   * allowed. e.g. PAN "RAJESH SHARMA" matches Bank "RAJESH KUMAR SHARMA".
   *
   * If false, both names must have the same number of tokens and
   * all must match (strict mode).
   *
   * Default: true
   */
  allowExtraTokens?: boolean;

  /**
   * Maximum number of extra (unmatched) tokens allowed in the
   * longer name. Only applies when allowExtraTokens is true.
   *
   * Default: 2
   */
  maxExtraTokens?: number;
}

const DEFAULT_CONFIG: Required<MatchConfig> = {
  allowExtraTokens: true,
  maxExtraTokens: 2,
};

// ─── CONSTANTS ───────────────────────────────────────────────────────────────

/** Honorifics stripped before comparison */
const HONORIFICS = new Set<string>([
  "mr", "mrs", "ms", "dr", "shri", "smt", "sri", "sh",
  "late", "prof", "kumari", "master", "ji", "bhai", "bai", "devi", "begum"
]);

// /** Common name suffixes in Indian names */
// const NAME_SUFFIXES = new Set<string>([
//   "ji", "bhai", "ben", "bai", "lal", "das", "devi", "kumari", "begum",
// ]);

/**
 * Known abbreviation → full form mappings.
 * Every expansion is explicit and auditable — no guessing.
 */
const KNOWN_ABBREVIATIONS: Record<string, string[]> = {
  md:    ["mohammad", "mohammed", "muhammed", "muhammad"],
  mohd:  ["mohammad", "mohammed", "muhammed", "muhammad"]
};

/**
 * Known alternate spellings of the same Indian name.
 * compareTokens checks both directions, so only one side needs to be listed.
 * Covers common transliteration variants (vowel/consonant substitution).
 */
const KNOWN_SPELLING_VARIANTS: Record<string, string[]> = {
  rai:      ["ray"],
  laxmi:    ["lakshmi", "laksmi"],
  lakshmi:  ["laxmi", "laksmi"],
  laksmi:   ["laxmi", "lakshmi"],
  narayan:  ["narayana"],
  narayana: ["narayan"],
  sinha:    ["singh", "simha"],
  simha:    ["sinha", "singh"],
  devi:     ["debi"],
  debi:     ["devi"]
};

/** Connector tokens that do not affect identity and are ignored */
const CONNECTOR_TOKENS = new Set<string>(["and", "&", "or"]);

/**
 * Relational markers in Indian bank account names.
 * Everything from this token onward is the father's/spouse's name
 * and should be truncated before matching.
 * Covers: S/O, SO (Son Of), D/O, DO (Daughter Of), W/O, WO (Wife Of)
 */
const RELATIONAL_MARKERS = new Set<string>(["so", "do", "wo", "fo", "co"]);

// ─── BUSINESS ENTITY CLEANUP ───────────────────────────────────────────────

/**
 * Business entity prefixes to strip from names.
 * These are organizational markers, not part of personal identity.
 */
const BUSINESS_ENTITY_PREFIXES = new Set<string>([
  "prop", "proprietor", "opto", "optd", "sr", "llp", "co", "ope", "company", "com", "pvt", "ltd", "limited", "group"
]);

// /**
//  * Business entity/descriptor suffixes to strip from names.
//  * These are operational descriptors, not part of personal identity.
//  */
// const BUSINESS_ENTITY_SUFFIXES = new Set<string>([
//   "agro", "krishi", "sewa", "seva", "khad", "beej", "bhandar",
//   "traders", "enterprise", "enterprises", "agency", "agencies",
//   "fertilizers", "fertiliser", "pesticides", "seeds", "center",
//   "centre", "depot", "store", "shop", "kendra", "kendram",
//   "services", "service", "pvt", "ltd", "limited", "group",
//   "industries", "manufacturing", "exports", "imports",
//   "commercial", "cooperative", "society", "association",
// ]);

/**
 * Remove business entity prefixes and suffixes from token array.
 * Preserves the order of remaining tokens.
 *
 * @param tokens - Array of name tokens
 * @returns Filtered array with business entities removed
 */
function removeBusinessEntities(tokens: string[]): string[] {
  return tokens.filter(
    (token) =>
      !BUSINESS_ENTITY_PREFIXES.has(token)
  );
}

/**
 * Attempt to split concatenated tokens like "VIJAYKUMAR" → ["VIJAY", "KUMAR"]
 * Uses otherTokens to find matching substrings within current tokens.
 * Repeatedly re-processes split fragments so tokens containing 3+ merged parts
 * can be decomposed (e.g. "VIJAYKUMARSHARMA" -> ["VIJAY", "KUMAR", "SHARMA"]).
 *
 * Key rule: a token (or any queue fragment) that already has an exact match in
 * otherTokens is NEVER split further — it is preserved as-is.  This prevents
 * valid tokens like "mali" from being incorrectly broken apart just because a
 * shorter token ("mal") appears as a substring.
 *
 * @param tokens - Cleaned name tokens to potentially split
 * @param otherTokens - Cleaned tokens from the other name (to match as substrings)
 * @returns Array with concatenated tokens split
 */
function splitConcatenatedTokens(
  tokens: string[],
  otherTokens: string[]
): string[] {
  // O(1) exact-match lookup for the "already matched" guard
  const otherSet = new Set(otherTokens);
  const result: string[] = [];

  for (const originalToken of tokens) {
    // ── Guard: token already has an exact match — preserve it unchanged ──
    if (otherSet.has(originalToken)) {
      result.push(originalToken);
      continue;
    }

    const queue: string[] = [originalToken];
    const finalParts: string[] = [];

    // Keep splitting from generated fragments until no more splits are possible.
    while (queue.length > 0) {
      const token = queue.shift()!;

      // ── Guard: fragment now has an exact match — stop splitting it ──
      if (otherSet.has(token)) {
        finalParts.push(token);
        continue;
      }

      let found = false;

      for (const otherToken of otherTokens) {
        if (otherToken.length < 3 || otherToken.length >= token.length) continue;

        const index = token.indexOf(otherToken);
        if (index === -1) continue;

        const prefix = token.substring(0, index);
        const suffix = token.substring(index + otherToken.length);

        if (prefix.length > 0 && suffix.length > 0) {
          // Token in middle: both residual fragments must be ≥2 chars to be meaningful.
          if (prefix.length < 2 || suffix.length < 2) continue;
          queue.push(prefix, suffix);
          found = true;
          break;
        }
        if (prefix.length > 0 && suffix.length === 0) {
          // Token at end: residual prefix must be ≥2 chars.
          if (prefix.length < 2) continue;
          queue.push(prefix, otherToken);
          found = true;
          break;
        }
        if (prefix.length === 0 && suffix.length > 0) {
          // Token at start: residual suffix must be ≥2 chars.
          if (suffix.length < 2) continue;
          queue.push(otherToken, suffix);
          found = true;
          break;
        }
      }

      if (!found) {
        finalParts.push(token);
      }
    }

    result.push(...finalParts);
  }

  return result;
}

// ─── FUZZY CHARACTER MATCH ───────────────────────────────────────────────────

/**
 * Build a character-frequency map for a token.
 */
function charFrequency(token: string): Record<string, number> {
  const freq: Record<string, number> = {};
  for (const ch of token) {
    freq[ch] = (freq[ch] ?? 0) + 1;
  }
  return freq;
}

/**
 * Fuzzy character-level comparison between two tokens.
 * Applied in Pass 3 to every shorter-side token still unmatched after passes 1 & 2.
 *
 * Accepts when (minimum token length 4 to avoid false positives):
 *  a) All characters are the same (anagram — zero character mismatches), OR
 *  b) At most 1 character is unaccounted for AND
 *     matched characters / max(len1, len2) > 0.60
 *
 * "Matched characters" = sum of min(freq1[c], freq2[c]) for every character c.
 * "Mismatches" = max(len1, len2) − matchedCharacters.
 */
function isFuzzyCharMatch(t1: string, t2: string): boolean {
  const MIN_TOKEN_LENGTH = 4;  // 3-char tokens have too few chars for safe 1-mismatch+60% rule
  if (t1.length < MIN_TOKEN_LENGTH || t2.length < MIN_TOKEN_LENGTH) return false;

  const freq1 = charFrequency(t1);
  const freq2 = charFrequency(t2);

  const allChars = new Set([...Object.keys(freq1), ...Object.keys(freq2)]);
  let matched = 0;
  for (const ch of allChars) {
    matched += Math.min(freq1[ch] ?? 0, freq2[ch] ?? 0);
  }

  const total = Math.max(t1.length, t2.length);
  const mismatches = total - matched;
  const matchedRatio = matched / total;

  // (a) anagram — all characters identical
  if (mismatches === 0) return true;

  // (b) at most 1 character difference AND >60 % similarity
  return mismatches <= 1 && matchedRatio > 0.6;
}

// ─── MERGED-INITIALS COMBINATION HELPER ──────────────────────────────────────

/**
 * Generate all C(arr.length, size) combinations of `size` elements from `arr`.
 * Preserves relative order within each combination.
 *
 * Used by Pass 4 to build candidate token groups of size 2 or 3.
 * With arr.length ≤ ~8 and size ≤ 3, the total count is ≤ C(8,3) = 56 —
 * negligible overhead.
 */
function generateCombinations<T>(arr: T[], size: number): T[][] {
  if (size === 0) return [[]];
  if (arr.length < size) return [];
  const result: T[][] = [];
  for (let i = 0; i <= arr.length - size; i++) {
    for (const tail of generateCombinations(arr.slice(i + 1), size - 1)) {
      result.push([arr[i], ...tail]);
    }
  }
  return result;
}

// ─── NORMALIZATION ───────────────────────────────────────────────────────────

/** Lowercase, convert separators to spaces, strip non-alpha, collapse whitespace */
function normalize(name: string): string {
  return name
    .toLowerCase()
    // Convert relational markers with slash (s/o, d/o, w/o) to single token before splitting
    .replace(/\b([sdw])\/o\b/g, "$1o")
    // Treat punctuation as token separators so initials like R.J remain split.
    .replace(/&/g, " and ")
    .replace(/[./,\\()-]+/g, " ")
    .replace(/[^a-z\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/** Tokenize a name, stripping honorifics and suffixes, truncating at relational markers */
function tokenize(name: string): string[] {
  const parts = normalize(name).split(" ");
  const filtered = parts
    .filter((p) => p.length > 0)
    .filter((p) => !HONORIFICS.has(p))
    .filter((p) => !CONNECTOR_TOKENS.has(p));

  // Truncate at relational marker (S/O, D/O, W/O) — everything after is a different person's name
  const markerIndex = filtered.findIndex((t) => RELATIONAL_MARKERS.has(t));
  return markerIndex === -1 ? filtered : filtered.slice(0, markerIndex);
}

// ─── TOKEN COMPARISON (DETERMINISTIC ONLY) ───────────────────────────────────

/**
 * Check if a short token is an initial/abbreviation of a longer token.
 *
 * Rules (all deterministic):
 *  - Single letter "R" matches any token starting with "r"
 *  - Two letters "ra" matches any token starting with "ra"
 *  - Known abbreviation "md" matches "mohammad", "mohammed", etc.
 */
function isInitialOrAbbreviation(
  short: string,
  long: string
): boolean {
  if (short.length === 0 || long.length === 0) return false;
  if (short.length >= long.length) return false;

  // Single or double letter initial: must match start of long token
  if (short.length <= 2 && long.startsWith(short)) return true;

  // Known abbreviation lookup
  const expansions = KNOWN_ABBREVIATIONS[short];
  if (expansions && expansions.includes(long)) return true;

  return false;
}

/**
 * Compare two tokens. Returns the match method if they match, or null.
 * Strictly deterministic — no scoring, no thresholds.
 */
function compareTokens(t1: string, t2: string): MatchMethod | null {
  // Rule 1: Exact match
  if (t1 === t2) return "exact";

  // Rule 2: One is an initial/prefix of the other
  if (isInitialOrAbbreviation(t1, t2)) return "initial_match";
  if (isInitialOrAbbreviation(t2, t1)) return "initial_match";

  // Rule 3: Known abbreviation (both directions)
  const t1Expansions = KNOWN_ABBREVIATIONS[t1];
  if (t1Expansions && t1Expansions.includes(t2)) return "known_abbreviation";
  const t2Expansions = KNOWN_ABBREVIATIONS[t2];
  if (t2Expansions && t2Expansions.includes(t1)) return "known_abbreviation";

  // Rule 4: Known alternate spelling variants (e.g., Rai ↔ Ray, Laxmi ↔ Lakshmi)
  const t1Variants = KNOWN_SPELLING_VARIANTS[t1] ?? [];
  const t2Variants = KNOWN_SPELLING_VARIANTS[t2] ?? [];
  if (t1Variants.includes(t2) || t2Variants.includes(t1)) return "known_variant";

  return null;
}

// ─── MAIN MATCH FUNCTION ────────────────────────────────────────────────────

/**
 * Match a PAN card name against a Bank account name.
 *
 * Logic (fully deterministic, auditable):
 *  1. Normalize both names (lowercase, strip punctuation)
 *  2. Remove honorifics (Mr, Smt, Shri, Dr, etc.)
 *  3. Remove suffixes (Ji, Bhai, Ben, Devi, etc.)
 *  4. Strip business entity prefixes and suffixes
 *  5. Split concatenated name tokens using tokens from both names
 *     (e.g., VIJAYKUMAR → VIJAY KUMAR if both exist in names)
 *  6. Tokenize into name parts
 *  7. For each token in the shorter name, find a deterministic
 *     match in the longer name (exact, initial, or known abbreviation)
 *  8. ACCEPT if ALL shorter-side tokens matched
 *     (no longer checks extra tokens on longer side)
 *
 * @param panName  - Name as printed on the PAN card
 * @param bankName - Name as registered on the bank account
 * @param config   - Optional configuration overrides
 */
export function matchNames(
  panName: string,
  bankName: string,
  config?: MatchConfig  // Kept for backward compatibility (no longer used)
): MatchResult {
  // Two-pass preprocessing to avoid redundant filtering:
  // Pass 1: Clean PAN tokens (tokenize + remove business entities)
  const panTokensRaw = tokenize(panName);
  const panTokensCleaned = removeBusinessEntities(panTokensRaw);

  // Pass 2: Clean Bank tokens (tokenize + remove business entities)
  const bankTokensRaw = tokenize(bankName);
  const bankTokensCleaned = removeBusinessEntities(bankTokensRaw);

  // Split concatenated tokens using cleaned tokens (no re-filtering)
  const panTokens = splitConcatenatedTokens(panTokensCleaned, bankTokensCleaned);
  const bankTokens = splitConcatenatedTokens(bankTokensCleaned, panTokensCleaned);

  const reasons: string[] = [];

  // ── Empty guard ──────────────────────────────────────────
  if (panTokens.length === 0 || bankTokens.length === 0) {
    return {
      verdict: "REJECT",
      matchedCount: 0,
      totalTokens: Math.max(panTokens.length, bankTokens.length),
      reordered: false,
      reasons: ["One or both names are empty after normalization"],
      details: [],
      panTokens,
      bankTokens,
    };
  }

  // ── Determine shorter / longer side ──────────────────────
  const isLongerPan = panTokens.length >= bankTokens.length;
  const longer = isLongerPan ? panTokens : bankTokens;
  const shorter = isLongerPan ? bankTokens : panTokens;
  const longerLabel = isLongerPan ? "PAN" : "Bank";
  const shorterLabel = isLongerPan ? "Bank" : "PAN";

  // ── Greedy token alignment ───────────────────────────────
  // Two passes: exact matches first, then abbreviation matches.
  // This prevents an abbreviation from "stealing" a slot that
  // should go to an exact match later.
  const details: TokenMatchDetail[] = [];
  const usedLongerIndices = new Set<number>();
  const matchedShorterTokens = new Set<string>();

  // Pass 1: exact matches
  for (const sToken of shorter) {
    for (let i = 0; i < longer.length; i++) {
      if (usedLongerIndices.has(i)) continue;
      if (sToken === longer[i]) {
        usedLongerIndices.add(i);
        matchedShorterTokens.add(sToken);
        details.push({
          panToken: isLongerPan ? longer[i] : sToken,
          bankToken: isLongerPan ? sToken : longer[i],
          method: "exact",
        });
        break;
      }
    }
  }

  // Pass 2: abbreviation & known abbreviation matches
  for (const sToken of shorter) {
    if (matchedShorterTokens.has(sToken)) continue;
    for (let i = 0; i < longer.length; i++) {
      if (usedLongerIndices.has(i)) continue;
      const method = compareTokens(sToken, longer[i]);
      if (method !== null && method !== "exact") {
        usedLongerIndices.add(i);
        matchedShorterTokens.add(sToken);
        details.push({
          panToken: isLongerPan ? longer[i] : sToken,
          bankToken: isLongerPan ? sToken : longer[i],
          method,
        });
        break;
      }
    }
  }

  // Pass 3: fuzzy character match — applied to ALL remaining unmatched shorter tokens.
  // For each unmatched shorter token, tries every unmatched longer-side token.
  // ACCEPT only if ALL shorter-side tokens end up matched after this pass.
  const unmatchedShorterAfterPass2 = shorter.filter(
    (t) => !matchedShorterTokens.has(t)
  );
  for (const sToken of unmatchedShorterAfterPass2) {
    for (let i = 0; i < longer.length; i++) {
      if (usedLongerIndices.has(i)) continue;
      if (isFuzzyCharMatch(sToken, longer[i])) {
        usedLongerIndices.add(i);
        matchedShorterTokens.add(sToken);
        details.push({
          panToken: isLongerPan ? longer[i] : sToken,
          bankToken: isLongerPan ? sToken : longer[i],
          method: "fuzzy_char_match",
        });
        break;
      }
    }
  }

  // Pass 4: Merged-initials reconciliation.
  // For every shorter-side token still unmatched after passes 1–3 whose length
  // is 2–3, generate all combinations of that many tokens from the pool of
  // still-unused longer-side tokens and check whether each character of the
  // merged token is the first letter of a distinct token in that combination
  // (order-insensitive). The first winning combination is accepted.
  //
  // Multiple unmatched shorter tokens are resolved independently (greedy):
  // each one updates usedLongerIndices before the next is tried.
  const MIN_MERGED_LEN = 2;
  const MAX_MERGED_LEN = 3;

  const unmatchedShorterAfterPass3 = shorter.filter(
    (t) => !matchedShorterTokens.has(t)
  );

  for (const sToken of unmatchedShorterAfterPass3) {
    if (sToken.length < MIN_MERGED_LEN || sToken.length > MAX_MERGED_LEN) continue;

    // Snapshot of unused longer tokens at this moment (updated after each hit)
    const unusedLongerWithIdx = longer
      .map((t, i) => ({ t, i }))
      .filter(({ i }) => !usedLongerIndices.has(i));

    if (unusedLongerWithIdx.length < sToken.length) continue;

    // Build all C(unusedCount, tokenLength) candidate groups
    const combos = generateCombinations(unusedLongerWithIdx, sToken.length);

    for (const combo of combos) {
      const tempUsed = new Set<number>();
      const mappings: Array<{ longerToken: string; longerIdx: number }> = [];
      let allMapped = true;

      for (const ch of sToken) {
        const found = combo.find(({ t, i }) => !tempUsed.has(i) && t.startsWith(ch));
        if (!found) { allMapped = false; break; }
        tempUsed.add(found.i);
        mappings.push({ longerToken: found.t, longerIdx: found.i });
      }

      if (allMapped) {
        matchedShorterTokens.add(sToken);
        for (const { longerToken, longerIdx } of mappings) {
          usedLongerIndices.add(longerIdx);
          details.push({
            panToken: isLongerPan ? longerToken : sToken,
            bankToken: isLongerPan ? sToken : longerToken,
            method: "merged_initials",
          });
        }
        break; // Stop on the first winning combination
      }
    }
  }

  // ── Evaluate result ──────────────────────────────────────
  // matchedCount uses matchedShorterTokens.size (not details.length) because
  // Pass 4 emits N detail entries for a single shorter-side merged-initial token.
  const matchedCount = matchedShorterTokens.size;
  const unmatchedShorter = shorter.length - matchedShorterTokens.size;
  const unmatchedLonger = longer.length - usedLongerIndices.size;
  const totalTokens = Math.max(panTokens.length, bankTokens.length);

  // Check reordering
  const panSorted = [...panTokens].sort().join(" ");
  const bankSorted = [...bankTokens].sort().join(" ");
  const reordered =
    panTokens.join(" ") !== bankTokens.join(" ") && panSorted === bankSorted;

  // ── Build reasons ────────────────────────────────────────
  for (const d of details) {
    switch (d.method) {
      case "exact":
        reasons.push(`"${d.panToken}" = "${d.bankToken}" → exact match`);
        break;
      case "initial_match":
        reasons.push(
          `"${d.panToken}" ↔ "${d.bankToken}" → initial/abbreviation of full name`
        );
        break;
      case "known_abbreviation":
        reasons.push(
          `"${d.panToken}" ↔ "${d.bankToken}" → known abbreviation expansion`
        );
        break;
      case "known_variant":
        reasons.push(
          `"${d.panToken}" ↔ "${d.bankToken}" → known alternate spelling variant`
        );
        break;
      case "fuzzy_char_match":
        reasons.push(
          `"${d.panToken}" ↔ "${d.bankToken}" → fuzzy character match (same/similar character composition, ≤1 char difference with >60% similarity)`
        );
        break;
      case "merged_initials": {
        const longerTok = isLongerPan ? d.panToken : d.bankToken;
        const mergedTok = isLongerPan ? d.bankToken : d.panToken;
        reasons.push(
          `"${d.panToken}" ↔ "${d.bankToken}" → merged initials ('${longerTok[0]}' in "${mergedTok}" is the initial of "${longerTok}")`
        );
        break;
      }
    }
  }

  if (reordered) {
    reasons.push("Name parts appear in different order (accepted)");
  }

  if (unmatchedLonger > 0) {
    const unmatchedTokens = longer.filter((_, i) => !usedLongerIndices.has(i));
    reasons.push(
      `${unmatchedLonger} extra token(s) in ${longerLabel}: "${unmatchedTokens.join('", "')}"`
    );
  }

  if (unmatchedShorter > 0) {
    const unmatchedTokens = shorter.filter((t) => !matchedShorterTokens.has(t));
    reasons.push(
      `${unmatchedShorter} token(s) in ${shorterLabel} could not be matched: "${unmatchedTokens.join('", "')}"`
    );
  }

  // ── Verdict (all-or-nothing, fully deterministic) ────────
  // SIMPLIFIED LOGIC (v3):
  // Accept if ALL shorter-side tokens matched (all core identity is present)
  // Extra tokens on longer side are generally business descriptors/metadata
  // and should NOT prevent matching
  let verdict: Verdict = "REJECT";

  if (unmatchedShorter === 0) {
    // ✅ All shorter-side tokens matched (core identity confirmed)
    verdict = "ACCEPT";
    reasons.push(`All ${shorterLabel} tokens matched`);

    if (unmatchedLonger > 0) {
      const unmatchedTokens = longer.filter((_, i) => !usedLongerIndices.has(i));
      reasons.push(
        `(Note: ${unmatchedLonger} extra token(s) in ${longerLabel}: "${unmatchedTokens.join('", "')}" - typically business descriptors)`
      );
    }
  } else {
    // ❌ Some shorter-side tokens don't match (core identity mismatch)
    reasons.push(
      `${unmatchedShorter} of ${shorter.length} ${shorterLabel} token(s) did not match → REJECT`
    );
  }

  return {
    verdict,
    matchedCount,
    totalTokens,
    reordered,
    reasons,
    details,
    panTokens,
    bankTokens,
  };
}

// ─── CONVENIENCE HELPER ──────────────────────────────────────────────────────

/** Quick boolean check — returns true if the names are an acceptable match */
export function isNameMatch(
  panName: string,
  bankName: string,
  config?: MatchConfig
): boolean {
  return matchNames(panName, bankName, config).verdict === "ACCEPT";
}

// ─── RULE REFERENCE EXPORT (for tooling / audit) ─────────────────────────────

/**
 * A snapshot of every token list and mapping used by the match engine.
 * Import this in dev tools or audit pages — never use it in production logic.
 * Adding a token here AND to the corresponding constant above is all that is
 * needed to extend a rule.
 */
export const MATCH_RULES = {
  /** Stripped from both names before any comparison */
  honorifics:             HONORIFICS,
  // /** Common Indian name suffixes stripped before comparison */
  // nameSuffixes:           NAME_SUFFIXES,
  /** Small connector words that carry no identity weight */
  connectorTokens:        CONNECTOR_TOKENS,
  /** Relational markers — everything after this token is a different person's name */
  relationalMarkers:      RELATIONAL_MARKERS,
  /** Organisational/business prefixes stripped from names */
  businessEntityPrefixes: BUSINESS_ENTITY_PREFIXES,
  // /** Operational descriptor suffixes stripped from names */
  // businessEntitySuffixes: BUSINESS_ENTITY_SUFFIXES,
  /** Short abbreviation → full-form expansions (e.g. md → mohammad) */
  knownAbbreviations:     KNOWN_ABBREVIATIONS,
  /** Alternate transliteration spellings of the same name (bidirectional) */
  knownSpellingVariants:  KNOWN_SPELLING_VARIANTS,
} as const;


